import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { Divider, EASE, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-ivory/20 bg-transparent px-4 py-3 text-sm text-ivory placeholder-ivory/30 outline-none transition-colors duration-500 focus:border-champagne";
const labelCls = "mb-2 block text-[10px] tracking-[0.3em] text-champagne uppercase";

const Field = ({ label, name, type = "text", required = false, value, onChange, testid }) => (
    <div>
        <label htmlFor={name} className={labelCls}>
            {label}
            {required && " *"}
        </label>
        <input
            id={name}
            name={name}
            type={type}
            required={required}
            value={value}
            onChange={onChange}
            data-testid={testid}
            className={inputCls}
        />
    </div>
);

const RequestAccess = () => {
    const { t, lang } = useLang();
    const r = t.request;
    const [form, setForm] = useState({
        name: "",
        company: "",
        role: "",
        email: "",
        phone: "",
        country: "",
        venue: "",
        message: "",
        website: "",
    });
    const [status, setStatus] = useState("idle");
    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/enquiries`, { ...form, lang });
            setStatus("done");
        } catch {
            setStatus("error");
        }
    };

    return (
        <>
            <section
                data-testid="statement-section"
                className="border-t border-champagne/15 bg-ivory px-6 py-20 text-center md:py-28"
            >
                <Reveal>
                    <h2 className="font-serif text-3xl leading-snug text-ink md:text-5xl" data-testid="statement-title">
                        {t.statement.title1}
                        <br />
                        {t.statement.title2}
                    </h2>
                    <div className="mt-6">
                        <Divider />
                    </div>
                    <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-ink/65">{t.statement.text}</p>
                </Reveal>
            </section>

            <section id="request" data-testid="request-section" className="bg-navy-deep px-6 py-20 md:py-28">
                <div className="mx-auto max-w-3xl">
                    <Reveal className="text-center">
                        <h2 className="font-serif text-3xl tracking-[0.18em] text-ivory uppercase md:text-4xl">
                            {r.title}
                        </h2>
                        <div className="mt-5">
                            <Divider dark />
                        </div>
                        <p className="mt-6 text-sm leading-relaxed text-ivory/65">{r.sub}</p>
                    </Reveal>

                    {status === "done" ? (
                        <motion.div
                            data-testid="request-success"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="mt-14 border border-champagne/30 px-8 py-14 text-center"
                        >
                            <p className="font-serif text-3xl italic text-champagne">{r.successTitle}</p>
                            <p className="mt-4 text-sm text-ivory/70">{r.successText}</p>
                        </motion.div>
                    ) : (
                        <Reveal delay={0.1}>
                            <form onSubmit={submit} data-testid="request-form" className="mt-12 grid gap-6 md:grid-cols-2">
                                <Field label={r.fields.name} name="name" required value={form.name} onChange={onChange} testid="field-name" />
                                <Field label={r.fields.company} name="company" value={form.company} onChange={onChange} testid="field-company" />
                                <Field label={r.fields.role} name="role" value={form.role} onChange={onChange} testid="field-role" />
                                <Field label={r.fields.email} name="email" type="email" required value={form.email} onChange={onChange} testid="field-email" />
                                <Field label={r.fields.phone} name="phone" type="tel" value={form.phone} onChange={onChange} testid="field-phone" />
                                <Field label={r.fields.country} name="country" value={form.country} onChange={onChange} testid="field-country" />
                                <div className="md:col-span-2">
                                    <label htmlFor="venue" className={labelCls}>
                                        {r.fields.venue}
                                    </label>
                                    <select
                                        id="venue"
                                        name="venue"
                                        value={form.venue}
                                        onChange={onChange}
                                        data-testid="field-venue"
                                        className={`${inputCls} appearance-none`}
                                    >
                                        <option value="" className="bg-navy-deep">
                                            —
                                        </option>
                                        {r.venues.map((v) => (
                                            <option key={v} value={v} className="bg-navy-deep">
                                                {v}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="message" className={labelCls}>
                                        {r.fields.message}
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={4}
                                        value={form.message}
                                        onChange={onChange}
                                        data-testid="field-message"
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                                <input
                                    type="text"
                                    name="website"
                                    value={form.website}
                                    onChange={onChange}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    data-testid="field-honeypot"
                                    className="absolute -left-[9999px] h-0 w-0 opacity-0"
                                />
                                <div className="text-center md:col-span-2">
                                    <button
                                        type="submit"
                                        disabled={status === "sending"}
                                        data-testid="request-submit"
                                        className="group inline-flex items-center gap-3 border border-champagne/60 px-8 py-3.5 text-[11px] tracking-[0.3em] text-champagne uppercase transition-colors duration-500 hover:bg-champagne hover:text-navy-deep disabled:opacity-50"
                                    >
                                        {r.cta}
                                        <ArrowRight
                                            size={14}
                                            strokeWidth={1.6}
                                            className="transition-transform duration-500 group-hover:translate-x-1.5"
                                        />
                                    </button>
                                    <p className="mt-5 font-serif text-sm italic text-ivory/45">{r.note}</p>
                                    {status === "error" && (
                                        <p data-testid="request-error" className="mt-3 text-xs tracking-[0.2em] text-red-300/80 uppercase">
                                            {r.error}
                                        </p>
                                    )}
                                </div>
                            </form>
                        </Reveal>
                    )}
                </div>
            </section>
        </>
    );
};

export default RequestAccess;
